import { apiSend, USE_MOCK_API } from './httpClient'
import { getTrip, updateTrip } from './trips.mock'
import { listPoints, updatePoint } from './points.mock'
import { ApiError } from './store'
import { addDays, tripDayCount } from '../lib/dates'

export type ScheduleMode = 'balanced' | 'packed' | 'relaxed' | 'min_travel' | 'weather'
export interface ScheduleCustom { active_start: number; active_end: number; break_start?: number | null;
  break_minutes?: number; max_walk_minutes?: number | null; car_available?: boolean; wish?: string }
export interface SchedulePlacement { visit_id: string; day_index: number; start_minute: number; duration_minutes: number }
export interface SchedulePreview { status: 'optimal' | 'feasible' | 'timeout' | 'invalid' | 'weather_unavailable';
  expected_updated_at: string; placements: SchedulePlacement[]; unplaced_must: string[];
  unplaced_optional: string[]; conflicts: string[]; provisional: string[]; explanation?: string }

export async function previewSchedule(tripId: string, mode: ScheduleMode, custom: ScheduleCustom): Promise<SchedulePreview> {
  if (!USE_MOCK_API) return apiSend('POST', `/trips/${tripId}/schedule/preview`, {mode, custom})
  const trip = await getTrip(tripId)
  if (!trip.start_date || !trip.end_date) throw new ApiError(400, 'Trip dates are required')
  const points = await listPoints(tripId)
  const days = tripDayCount(trip.start_date, trip.end_date)
  const cursor = Array.from({length:days},()=>custom.active_start)
  const placements: SchedulePlacement[]=[]
  const unplaced_must:string[]=[]; const unplaced_optional:string[]=[]
  for (const p of points) {
    if (p.timing === 'stay') continue
    const day = p.day_index >= 0 ? p.day_index : cursor.indexOf(Math.min(...cursor))
    const start = p.timing === 'exact' && p.time ? Number(p.time.slice(0,2))*60+Number(p.time.slice(3,5)) : cursor[day]
    const duration = p.duration_minutes ?? 60
    if (day >= days || start + duration > (p.timing === 'exact' ? 2880 : custom.active_end)) {
      (p.priority === 'optional' ? unplaced_optional : unplaced_must).push(p.id); continue
    }
    placements.push({visit_id:p.id,day_index:day,start_minute:start,duration_minutes:duration})
    cursor[day] = Math.max(cursor[day], start+duration+(mode==='relaxed'?45:15))
  }
  return {status:'feasible',expected_updated_at:trip.updated_at,placements,unplaced_must,unplaced_optional,
    conflicts:[],provisional:['Demo preview: travel times and opening hours are not verified']}
}

export async function applySchedule(tripId: string, preview: SchedulePreview): Promise<void> {
  if (!USE_MOCK_API) { await apiSend('POST', `/trips/${tripId}/schedule/apply`, preview); return }
  const trip=await getTrip(tripId)
  if (trip.updated_at!==preview.expected_updated_at) throw new ApiError(409,'Trip changed; calculate a new preview')
  for (const p of preview.placements) {
    const hour=String(Math.floor(p.start_minute/60)).padStart(2,'0')
    const minute=String(p.start_minute%60).padStart(2,'0')
    await updatePoint(tripId,p.visit_id,{day_index:p.day_index,date:addDays(trip.start_date!,p.day_index),
      time:`${hour}:${minute}`,duration_minutes:p.duration_minutes})
  }
  await updateTrip(tripId,{description:trip.description})
}
